import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/ScrollReveal";
import { X, Check, ArrowRight, ArrowDown } from "lucide-react";

import ReliabilityCore from "@/components/ReliabilityCore";

const WhyDevCoreSection = () => {
  const { t } = useTranslation();
  const rows = t("why.rows", { returnObjects: true }) as {
    problem: string;
    solution: string;
  }[];
  return (
    <section id="why" className="relative pt-16 sm:pt-24 md:pt-28 pb-12 md:pb-16 bg-background overflow-hidden">
      <div className="absolute inset-0 tech-grid opacity-[0.1] pointer-events-none" />

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-3 gap-10 lg:gap-12 items-center mb-12 md:mb-16">
          <div className="lg:col-span-2">
            <ScrollReveal>
              <h2 className="text-[1.55rem] sm:text-3xl md:text-5xl font-bold text-foreground leading-[1.1] tracking-tight">
                {t("why.titlePrefix")}{" "}
                <span className="text-accent">
                  {t("why.titleHighlight")}
                </span>{" "}
                {t("why.titleSuffix")}
              </h2>
              <p className="mt-6 text-muted-foreground text-[15px] sm:text-lg md:text-xl leading-relaxed max-w-2xl">
                {t("why.body")}
              </p>
            </ScrollReveal>
          </div>
          <ScrollReveal delay={150} direction="left" className="hidden lg:block">
            <ReliabilityCore />
          </ScrollReveal>
        </div>


        <div className="relative group">
          {/* ambient aurora glows */}
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-accent/10 blur-[120px] rounded-full pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 w-96 h-96 bg-destructive/[0.06] blur-[120px] rounded-full pointer-events-none" />

          {/* ===================== DESKTOP / TABLET TABLE ===================== */}
          <div className="relative hidden md:block overflow-hidden rounded-3xl border border-border bg-card/50 backdrop-blur-xl shadow-2xl">
            {/* column headers */}
            <div className="grid grid-cols-2 border-b border-border">
              <div className="flex items-center gap-3 px-8 py-5 bg-foreground/[0.02] border-r border-border">
                <span className="flex items-center justify-center w-5 h-5 rounded-full border border-destructive/40">
                  <span className="w-1.5 h-1.5 rounded-full bg-destructive shadow-[0_0_8px_hsl(var(--destructive)/0.6)]" />
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  the_usual_way
                </span>
              </div>
              <div className="relative flex items-center gap-3 px-8 py-5 bg-accent/[0.05] overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-accent/0 via-accent/5 to-transparent pointer-events-none" />
                <span className="relative z-10 flex items-center justify-center w-5 h-5 rounded-full border border-accent/40">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_12px_hsl(var(--accent))]" />
                </span>
                <span className="relative z-10 font-mono text-[11px] uppercase tracking-[0.18em] text-accent">
                  the_devcore_way
                </span>
              </div>
            </div>

            {/* comparison rows */}
            <div className="divide-y divide-border/60">
              {rows.map((r, i) => (
                <ScrollReveal key={r.problem} delay={i * 80}>
                  <div className="group/row grid grid-cols-2 transition-colors duration-300 hover:bg-foreground/[0.01]">
                    {/* problem */}
                    <div className="flex items-start gap-4 px-8 py-6">
                      <X size={18} className="text-destructive/50 mt-0.5 shrink-0" strokeWidth={2.5} />
                      <p className="text-base text-muted-foreground line-through decoration-destructive/30 decoration-1 font-light">
                        {r.problem}
                      </p>
                    </div>
                    {/* solution */}
                    <div className="relative flex items-start gap-4 px-8 py-6 bg-accent/[0.025] border-l border-border/60">
                      <div className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-transparent via-accent/40 to-transparent opacity-0 group-hover/row:opacity-100 transition-opacity duration-500" />
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-accent/10 border border-accent/30 mt-0.5 shrink-0 transition-[background-color,box-shadow] duration-200 group-hover/row:bg-accent/25 group-hover/row:shadow-[0_0_12px_hsl(var(--accent)/0.4)]">
                        <Check size={12} className="text-accent" strokeWidth={3} />
                      </span>
                      <p className="text-base text-foreground font-medium">{r.solution}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>

          {/* ===================== MOBILE PAIRED CARDS ===================== */}
          <div className="md:hidden">
            {/* legend above the cards */}
            <div className="grid grid-cols-2 gap-3 mb-5 px-1">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-4 h-4 rounded-full border border-destructive/40 shrink-0">
                  <span className="w-1 h-1 rounded-full bg-destructive" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  the_usual_way
                </span>
              </div>
              <div className="flex items-center justify-end gap-2">
                <span className="flex items-center justify-center w-4 h-4 rounded-full border border-accent/40 shrink-0">
                  <span className="w-1 h-1 rounded-full bg-accent shadow-[0_0_8px_hsl(var(--accent))]" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent">
                  the_devcore_way
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              {rows.map((r, i) => (
                <ScrollReveal key={r.problem} delay={i * 70}>
                  <div className="rounded-2xl border border-border bg-card/50 backdrop-blur-xl overflow-hidden shadow-lg">
                    {/* problem */}
                    <div className="px-5 pt-5 pb-7">
                      <div className="flex items-start gap-3">
                        <X size={16} className="text-destructive/50 mt-0.5 shrink-0" strokeWidth={2.5} />
                        <p className="text-sm text-muted-foreground line-through decoration-destructive/30 decoration-1 font-light">
                          {r.problem}
                        </p>
                      </div>
                    </div>

                    {/* solution — single divider with arrow straddling it */}
                    <div className="relative px-5 pt-8 pb-5 bg-accent/[0.04] border-t border-border/60">
                      <span
                        className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10 flex items-center justify-center w-7 h-7 rounded-full border border-accent/30 text-accent shadow-[0_0_12px_hsl(var(--accent)/0.25)]"
                        style={{ background: "hsl(var(--card))" }}
                      >
                        <ArrowDown size={14} strokeWidth={2.5} />
                      </span>

                      <div className="flex items-start gap-3">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-accent/15 border border-accent/30 mt-0.5 shrink-0">
                          <Check size={12} className="text-accent" strokeWidth={3} />
                        </span>
                        <p className="text-sm text-foreground font-medium">{r.solution}</p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>

        </div>

        <ScrollReveal delay={200} className="mt-12 md:mt-16 flex justify-center md:justify-end">
          <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
            <a
              href="/contact"
              className="group relative inline-flex w-full sm:w-auto justify-center items-center gap-3 px-8 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium overflow-hidden transition-transform duration-200 active:scale-[0.97]"

            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-foreground/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative z-10">{t("why.cta")}</span>
              <ArrowRight size={18} className="relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </span>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default WhyDevCoreSection;
